export type ImportTypeKey = 'customerContacts' | 'customerCollectionSettings';

export type ImportSessionStatus =
  | 'Uploaded'
  | 'Validating'
  | 'Validated'
  | 'Rejected'
  | 'Committing'
  | 'Committed'
  | 'Failed'
  | 'Cancelled';

export interface ImportTarget {
  companyId: number;
  companyName: string;
  companyGroupId: number;
  companyGroupName: string;
}

export interface ImportSession {
  publicId: string;
  importType: ImportTypeKey;
  status: ImportSessionStatus;
  fileName: string;
  fileSizeBytes: number;
  totalRows: number;
  errorCount: number;
  warningCount: number;
  insertedCount: number;
  updatedCount: number;
  skippedDuplicateCount: number;
  failureReason: string | null;
  createdByUserName: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface ImportRowIssue {
  rowNumber: number;
  severity: 'Error' | 'Warning';
  fieldName: string | null;
  fieldValue: string | null;
  message: string;
}

export interface ImportUploadResult {
  publicId: string;
  importType: ImportTypeKey;
  status: ImportSessionStatus;
}

export const IMPORT_TYPE_LABELS: Record<ImportTypeKey, string> = {
  customerContacts: 'Customer contacts',
  customerCollectionSettings: 'Customer collection settings',
};

/**
 * Statuses where the import is still working, and so holds the company group's only import slot.
 * A validation that found errors ends as 'Rejected' rather than waiting, because the workbook has
 * to be corrected and uploaded again.
 */
const ACTIVE_IMPORT_STATUSES: ImportSessionStatus[] = ['Uploaded', 'Validating', 'Validated', 'Committing'];

export function isImportActive(session: ImportSession): boolean {
  return ACTIVE_IMPORT_STATUSES.includes(session.status);
}

/**
 * Cancelling only makes sense while the import is still being checked. Once it has validated
 * cleanly it is already being applied, and once it has errors it has ended as 'Rejected'.
 */
export function isImportCancellable(session: ImportSession): boolean {
  return session.status === 'Uploaded' || session.status === 'Validating';
}

/** A clean validation is applied within seconds of the upload, so this is generous. */
const APPLY_OVERDUE_AFTER_MS = 5 * 60 * 1000;

/**
 * A workbook that validates without errors is applied for the user, so there is normally nothing to
 * press. One left at 'Validated' long after it was uploaded means that apply never arrived, and the
 * manual apply is the way back from it.
 */
export function isImportApplyOverdue(session: ImportSession, now: number = Date.now()): boolean {
  return session.status === 'Validated' && now - new Date(session.createdAt).getTime() > APPLY_OVERDUE_AFTER_MS;
}
